import {
  type CreateLockerverseAuctionOptions,
  createLockerverseAuctionClient,
  type LockerverseAuction,
  type LockerverseAuctionClient,
  type LockerverseSdkError,
} from "@lockerverse/sdk/auctions";
import { useCallback, useEffect, useRef, useState } from "react";
import { useImmutableResourceIdentity } from "./immutable-resource-identity.ts";

export type UseLockerverseAuctionsResult = {
  client: LockerverseAuctionClient;
  auctions: LockerverseAuction[] | null;
  loading: boolean;
  error: LockerverseSdkError | null;
  refresh: () => void;
};

type AuctionLoadState = Pick<
  UseLockerverseAuctionsResult,
  "auctions" | "error"
>;

export function useLockerverseAuctions(
  options: CreateLockerverseAuctionOptions
): UseLockerverseAuctionsResult {
  useImmutableResourceIdentity("auctions", {
    apiBaseUrl: options.apiBaseUrl,
    communitySlug: options.communitySlug,
    environment: options.environment,
    mediaBaseUrl: options.mediaBaseUrl,
    slug: "",
  });
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const [client] = useState(() =>
    createLockerverseAuctionClient({
      ...options,
      onError: (error) => onErrorRef.current?.(error),
    })
  );
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<AuctionLoadState>({
    auctions: null,
    error: null,
  });
  useEffect(() => {
    let active = true;
    client.list().then(
      (auctions) => {
        if (active) {
          setState({ auctions, error: null });
        }
      },
      (error: LockerverseSdkError) => {
        if (active) {
          setState({ auctions: null, error });
        }
      }
    );
    return () => {
      active = false;
    };
  }, [client, revision]);
  const refresh = useCallback(() => {
    setState({ auctions: null, error: null });
    setRevision((value) => value + 1);
  }, []);
  return {
    ...state,
    client,
    loading: state.auctions === null && state.error === null,
    refresh,
  };
}
